<template>
  <div>
    <div class="row mb-1">
      <div class="col-md-12 d-flex justify-content-end align-items-center">
        <small v-if="statusGlpi" class="text-muted mr-1">
          <template v-if="!statusGlpi.configurado">GLPI não configurado no .env</template>
          <template v-else>
            GLPI: {{ statusGlpi.localizacoes }} localizações ·
            {{ statusGlpi.unidades_sem_localizacao }} sem localização ·
            {{ statusGlpi.ultima_sincronizacao ? "sincronizado em " + formatarData(statusGlpi.ultima_sincronizacao) : "nunca sincronizado" }}
          </template>
        </small>
        <button
          class="btn btn-sm btn-outline-info mr-50"
          :disabled="sincronizando || !statusGlpi || !statusGlpi.configurado"
          @click="sincronizarGlpi"
        >
          <i class="fa fa-sync" :class="{ 'fa-spin': sincronizando }"></i> Sincronizar com GLPI
        </button>
        <button class="btn btn-sm btn-outline-success mr-50" :disabled="exportando" @click="exportarExcel">
          <i class="fa fa-file-excel"></i> {{ exportando ? "Exportando..." : "Exportar Excel" }}
        </button>
        <button class="btn btn-sm btn-outline-primary" :disabled="importando" @click="$refs.arquivoImportacao.click()">
          <i class="fa fa-upload"></i> {{ importando ? "Importando..." : "Importar Excel" }}
        </button>
        <input
          ref="arquivoImportacao"
          type="file"
          accept=".xlsx,.xls,.csv"
          class="d-none"
          @change="importarExcel"
        />
      </div>
    </div>

    <b-modal v-model="mostrarResultado" title="Resultado da importação" ok-only ok-title="Fechar" size="lg">
      <template v-if="resultadoImportacao">
        <p class="mb-50">
          <span class="badge badge-success mr-50">{{ resultadoImportacao.criados }} criada(s)</span>
          <span class="badge badge-primary mr-50">{{ resultadoImportacao.atualizados }} atualizada(s)</span>
          <span class="badge badge-secondary mr-50">{{ resultadoImportacao.sem_alteracao }} sem alteração</span>
          <span v-if="resultadoImportacao.ignorados.length" class="badge badge-light-secondary mr-50">
            {{ resultadoImportacao.ignorados.length }} ignorada(s)
          </span>
          <span v-if="resultadoImportacao.erros.length" class="badge badge-danger">
            {{ resultadoImportacao.erros.length }} com erro
          </span>
        </p>
        <p v-if="resultadoImportacao.ignorados.length" class="text-muted">
          Ignoradas porque o ID não existe: linha(s) {{ resultadoImportacao.ignorados.join(", ") }}.
        </p>
        <template v-if="resultadoImportacao.erros.length">
          <p class="mb-50">Estas linhas <strong>não foram gravadas</strong> - corrija e importe de novo:</p>
          <table class="table table-sm table-bordered mb-0">
            <thead>
              <tr><th style="width: 80px;">Linha</th><th>Problema</th></tr>
            </thead>
            <tbody>
              <tr v-for="erro in resultadoImportacao.erros" :key="erro.linha + erro.motivo">
                <td>{{ erro.linha }}</td>
                <td>{{ erro.motivo }}</td>
              </tr>
            </tbody>
          </table>
        </template>
      </template>
    </b-modal>

    <b-modal v-model="mostrarSincronizacao" title="Sincronização com o GLPI" ok-only ok-title="Fechar" size="lg">
      <template v-if="resultadoSincronizacao">
        <p class="mb-50">
          {{ resultadoSincronizacao.localizacoes }} localizações e {{ resultadoSincronizacao.entidades }} entidades atualizadas.
        </p>
        <p class="mb-50">
          <span class="badge badge-success mr-50">{{ resultadoSincronizacao.unidades_criadas }} unidade(s) criada(s)</span>
          <span class="badge badge-primary mr-50">{{ resultadoSincronizacao.unidades_vinculadas }} ligada(s) pelo nome</span>
          <span v-if="resultadoSincronizacao.removidas" class="badge badge-warning">
            {{ resultadoSincronizacao.removidas }} localização(ões) removida(s) no GLPI
          </span>
        </p>
        <template v-if="resultadoSincronizacao.possiveis_duplicadas.length">
          <p class="mb-50">
            <strong>Possíveis duplicatas</strong> - unidade criada agora com nome parecido com uma que você já tinha (sem localização).
            Se for a mesma, ligue a antiga à localização e apague a nova (ela não volta a ser criada):
          </p>
          <table class="table table-sm table-bordered mb-0">
            <thead>
              <tr><th>Criada agora</th><th>Já existente</th><th style="width: 90px;">Parecida</th></tr>
            </thead>
            <tbody>
              <tr v-for="par in resultadoSincronizacao.possiveis_duplicadas" :key="par.nova.id + '-' + par.existente.id">
                <td>#{{ par.nova.id }} {{ par.nova.nome }}</td>
                <td>#{{ par.existente.id }} {{ par.existente.nome }}</td>
                <td>{{ par.similaridade }}%</td>
              </tr>
            </tbody>
          </table>
        </template>
      </template>
    </b-modal>

    <div class="row">
      <div class="col-md-12">
        <base-crud
          :ref="'unidadesCrud'"
          crudName="Unidades"
          newText="Nova Unidade"
          :table="table"
          :columns="columns"
          :options="options"
          :endPoint="endPoint"
          :enableAdd="true"
          :enableEdit="true"
          :enableDelete="true"
          :enableView="false"
          :enableReset="false"
          :dataForm="dataForm"
        >
          <div slot="status" slot-scope="props">
            <span v-if="props.row.status == 1" class="badge badge-success">Ativo</span>
            <span v-else class="badge badge-secondary">Inativo</span>
          </div>
          <div slot="glpi_localizacao" slot-scope="props">
            <template v-if="props.row.glpi_localizacao">
              {{ props.row.glpi_localizacao.nome_completo }}
              <span v-if="props.row.glpi_localizacao.removido_em" class="badge badge-warning ml-25">removida no GLPI</span>
            </template>
            <span v-else class="text-muted">-</span>
          </div>
          <div slot="tipo_unidade" slot-scope="props">
            {{ props.row.tipo_unidade ? props.row.tipo_unidade.nome : "-" }}
          </div>
        </base-crud>
      </div>
    </div>
  </div>
</template>

<script>
import { BModal } from "bootstrap-vue";
import BaseCrud from "../../layouts/Base/BaseCrud";

export default {
  data: function () {
    return {
      table: "unidadesTable",
      exportando: false,
      importando: false,
      sincronizando: false,
      mostrarSincronizacao: false,
      resultadoSincronizacao: null,
      statusGlpi: null,
      mostrarResultado: false,
      resultadoImportacao: null,
      unidadesCrud: {},
      pages: [],
      url: "",
      columns: ["id", "codigo_cliente", "nome", "tipo_unidade", "glpi_localizacao", "status", "actions"],
      tableData: ["id", "codigo_cliente", "nome", "tipo_unidade", "glpi_localizacao", "status"],
      options: {
        filterByColumn: true,
        debounce: 1000,
        filterable: ["codigo_cliente", "nome", "tipo_unidade", "status"],
        pagination: { chunk: 10, dropdown: false, nav: "scroll" },
        perPage: 10,
        perPageValues: [10, 25, 50, 100],
        headings: {
          id: "ID",
          codigo_cliente: "Código do cliente",
          nome: "Unidade",
          tipo_unidade: "Tipo de Unidade",
          glpi_localizacao: "Localização GLPI",
          status: "Status",
          actions: "Ações",
        },

        texts: {
          filterBy: "Filtrar",
          defaultOption: "Selecione",
        },
        listColumns: {
          status: [
            { id: 1, text: "Ativo" },
            { id: 0, text: "Inativo" },
          ],
          tipo_unidade: [],
        },
        templates: {

        },
        requestFunction: function (data) {
          let requestData = {};

          let query = this.$parent.$parent.$parent.query(data.query);
          requestData.params = data;
          requestData.params.query = "";
          requestData.params.with = ["tipoUnidade", "glpiLocalizacao"];

          return axios.get(this.url + "?" + query, requestData).catch(
            function (e) {
              this.dispatch("error", e);
            }.bind(this)
          );
        },
      },
      endPoint: "unidades/",
      dataForm: {},
    };
  },
  components: {
    BaseCrud,
    BModal,
  },
  methods: {
    carregarStatusGlpi() {
      axios
        .get(this.$store.state.api + "glpi/status")
        .then((resposta) => { this.statusGlpi = resposta.data; })
        .catch(() => { this.statusGlpi = null; });
    },
    async sincronizarGlpi() {
      this.sincronizando = true;
      try {
        const { data } = await axios.post(this.$store.state.api + "glpi/sincronizar");
        this.resultadoSincronizacao = data;
        this.mostrarSincronizacao = true;
        this.carregarStatusGlpi();
        this.$refs.unidadesCrud.$refs.table.refresh();
      } catch (error) {
        const mensagem = error.response && typeof error.response.data === "string" ? error.response.data : "Falha ao sincronizar com o GLPI.";
        this.$message("Ops", mensagem, "error");
      } finally {
        this.sincronizando = false;
      }
    },
    formatarData(iso) {
      return new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
    },
    async exportarExcel() {
      this.exportando = true;
      try {
        const resposta = await axios.get(this.$store.state.api + "unidades/exportar", { responseType: "blob" });
        const nomeArquivo = (resposta.headers["content-disposition"] || "").match(/filename="?([^";]+)"?/);
        const url = URL.createObjectURL(resposta.data);
        const link = document.createElement("a");
        link.href = url;
        link.download = nomeArquivo ? nomeArquivo[1] : "unidades.xlsx";
        link.click();
        URL.revokeObjectURL(url);
      } catch (error) {
        this.$message("Ops", "Não foi possível exportar as unidades.", "error");
      } finally {
        this.exportando = false;
      }
    },
    async importarExcel(evento) {
      const arquivo = evento.target.files[0];
      if (!arquivo) return;

      const dadosFormulario = new FormData();
      dadosFormulario.append("arquivo", arquivo);

      this.importando = true;
      try {
        const resposta = await axios.post(this.$store.state.api + "unidades/importar", dadosFormulario);
        this.resultadoImportacao = resposta.data;
        this.mostrarResultado = true;
        this.$refs.unidadesCrud.$refs.table.refresh();
      } catch (error) {
        const erros = error.response && error.response.data && error.response.data.arquivo;
        this.$message("Ops", erros ? erros[0] : "Houve um erro durante a importação da planilha.", "error");
      } finally {
        this.importando = false;
        this.$refs.arquivoImportacao.value = "";
      }
    },
    query: function (query) {
      let columns = {
        id: "id",
        codigo_cliente: "codigo_cliente",
        nome: "nome",
        status: "status",
        tipo_unidade: "tipo_unidade_id",
      };
      let filters = "";
      $.each(query, function (index, value) {
        filters += columns[index] + "=" + value + "&";
      });
      return filters;
    },
    getTipoUnidades: function () {
      const self = this;
      const api = self.$store.state.api + "tipo_unidades?paginated=false";

      axios
        .get(api)
        .then((response) => {
          self.options.listColumns.tipo_unidade = (response.data.data || []).map(
            (tipo_unidade) => ({ id: tipo_unidade.id, text: tipo_unidade.nome })
          );
        })
        .catch(() => {});
    },
  },
  mounted: function () {
    this.getTipoUnidades();
    this.carregarStatusGlpi();
  },
};
</script>

<style scoped>
.VuePagination {
  text-align: center;
}
.uploading-image {
  display: flex;
  border-radius: 10px;
  width: 100px;
  height: 100px;
}
</style>